import React, {
  useMemo,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Grid, FormSelectV2, FormCheckbox, Tooltip, Icon } from '@lionstep/ui';

// Styles
import styles from './generateTextDrawer.module.scss';

const schema = yup.object().shape({
  language: yup.object().shape().nullable(),
  approach: yup
    .object()
    .shape()
    .nullable()
    .when('language', {
      is: (language) => Boolean(language),
      then: () =>
        yup.object().shape().nullable().required('Approach is required'),
      otherwise: () => yup.object().shape().nullable().notRequired(),
    }),
});

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English', key: 'en' },
  { value: 'de', label: 'German', key: 'de' },
  { value: 'fr', label: 'French', key: 'fr' },
  { value: 'it', label: 'Italian', key: 'it' },
  { value: 'es', label: 'Spanish', key: 'es' },
];

const APPROACH_OPTIONS = [
  {
    value: 'FORMAL',
    label: 'Formal',
    support: ['de', 'fr', 'es', 'it'],
    key: 'FORMAL',
  },
  {
    value: 'INFORMAL',
    label: 'Informal',
    support: ['de', 'fr', 'es', 'it'],
    key: 'INFORMAL',
  },
  { value: 'UNIVERSAL', label: 'Universal', support: ['en'], key: 'UNIVERSAL' },
];

const DIRECTIVE_OPTIONS = [
  {
    value: 'addDirectAssignmentNote',
    key: 'addDirectAssignmentNote',
    text: 'Add direct assignment note',
    tooltipContent:
      'Adds a note to the job description indicating that this is a direct assignment.',
  },
  {
    value: 'doNotRefineTexts',
    key: 'doNotRefineTexts',
    text: 'Do not refine texts',
    tooltipContent:
      'Keep the original text exactly as it is, without changing wording, style, or number of items.',
  },
  {
    value: 'noEmojis',
    key: 'noEmojis',
    text: 'No emojis',
    tooltipContent:
      'Remove all emojis from the text, even if they appear in templates.',
  },
  // {
  //   value: 'skipAdditionalBenefits',
  //   key: 'skipAdditionalBenefits',
  //   text: 'Skip additional benefits',
  //   tooltipContent: "Leave the 'Additional Benefits' section empty.",
  // },
];

const DirectiveLabel = ({ text, tooltipId, tooltipContent }) => (
  <>
    {text}
    <Tooltip content={tooltipContent} id={tooltipId} className={styles.tooltip}>
      <Icon name="WarningCircle" className={styles.infoIcon} />
    </Tooltip>
  </>
);

const GenerateTextForm = forwardRef(({ onSubmit, isGeneratingText }, ref) => {
  const formMethods = useForm({
    defaultValues: {
      language: '',
      approach: '',
      directives: ['addDirectAssignmentNote'],
    },
    resolver: yupResolver(schema),
  });

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    watch,
  } = formMethods;

  const watchLanguage = watch('language');
  const watchApproach = watch('approach');

  const handleLanguageChange = useCallback(
    ({ value }) => {
      if (value === 'en') {
        setValue(
          'approach',
          APPROACH_OPTIONS.find((option) => option.value === 'UNIVERSAL'),
        );
        setError('approach', null);
      } else if (
        ['de', 'fr'].includes(value) &&
        watchApproach?.value === 'UNIVERSAL'
      ) {
        setValue('approach', null);
      }
    },
    [setValue, setError, watchApproach],
  );

  const filteredApproachOptions = useMemo(() => {
    const selectedLanguage = watchLanguage?.value;
    return APPROACH_OPTIONS.filter((option) =>
      option.support.includes(selectedLanguage),
    );
  }, [watchLanguage?.value]);

  const directiveOptions = useMemo(
    () =>
      DIRECTIVE_OPTIONS.map(({ value, text, tooltipContent }) => ({
        key: value,
        value,
        label: (
          <DirectiveLabel
            text={text}
            tooltipId={value}
            tooltipContent={tooltipContent}
          />
        ),
      })),
    [],
  );

  const handleFormSubmit = useCallback(
    (formData) => {
      const data = {
        directives: {
          add_direct_assignment_note: formData.directives.includes(
            'addDirectAssignmentNote',
          ),
          do_not_refine_texts: formData.directives.includes('doNotRefineTexts'),
          no_emojis: formData.directives.includes('noEmojis'),
          // skip_additional_benefits: formData.directives.includes(
          //   'skipAdditionalBenefits',
          // ),
        },
      };

      if (formData.language) {
        data.directives.language_form_of_address_override = {
          language: formData.language.value,
          form_of_address: formData.approach.value,
        };
      }

      onSubmit(data);
    },
    [onSubmit],
  );

  // Expose submit function to parent via ref
  useImperativeHandle(
    ref,
    () => ({
      submit: () => handleSubmit(handleFormSubmit)(),
    }),
    [handleSubmit, handleFormSubmit],
  );

  return (
    <FormProvider {...formMethods}>
      <form>
        <Grid.Row gutter={16}>
          <Grid.Col xs={12}>
            <FormSelectV2
              name="language"
              label="Language"
              placeholder="Use detected language"
              options={LANGUAGE_OPTIONS}
              onChange={handleLanguageChange}
              error={errors.language?.message}
              disabled={isGeneratingText}
            />
          </Grid.Col>

          <Grid.Col xs={12}>
            <FormSelectV2
              name="approach"
              label="Approach"
              placeholder="Use detected approach"
              options={filteredApproachOptions}
              error={errors.approach?.message}
              disabled={isGeneratingText || !watchLanguage?.value}
            />
          </Grid.Col>
          <Grid.Col xs={24}>
            <FormCheckbox
              name="directives"
              options={directiveOptions}
              error={errors.directives?.message}
              disabled={isGeneratingText}
              renderTemplate={(elements) => (
                <Grid.Row gutter={16}>
                  {elements.map((element) => (
                    <Grid.Col key={element.props.value} xs={24}>
                      {element}
                    </Grid.Col>
                  ))}
                </Grid.Row>
              )}
            />
          </Grid.Col>
        </Grid.Row>
      </form>
    </FormProvider>
  );
});

GenerateTextForm.displayName = 'GenerateTextForm';

export default GenerateTextForm;
